import React, {useCallback, useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {
  FormControlLabel, DialogTitle, DialogActions, SvgIcon, Divider, DialogContent, Backdrop, Box, FormHelperText, Tab,
} from '@mui/material';
import {Controller, FormProvider, useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {
  AccountInfoContainer, ModalContentContainer, RowEmailAdress, RowNameAndService, TypographyDesc, TypographyTitle,
} from './Style';
import {getAvailabilityPreferenceSchema, getAvailabilityPreferenceDefault, CONFLICT_SETTING_VIEW} from './yupSchema';

import {iconMap, serviceNameMap} from '../AccountSettings';

import {ZmPrimaryBtn, ZmSecondaryBtn} from 'Components/ZmButtons';
import {ZmCheckbox} from 'Components/ZmCheckbox';
import {ZmDialog} from 'Components/ZmDialogs';
import {ZmSliderTabs} from 'Components/ZmTabs';
import {updateAvailabilityPreferences} from 'Store/IntegrationStore';
import {MAX_CHECK_CALENDARS, PROVIDER_TYPE} from 'Utils/consts';

/**
 * Component to manage Availability Preferences / Conflict Settings
 * @param {Object} props
 * @param {Account} props.account
 * @return {ReactElement}
 */
export default function AvailabilityPreference(props) {
  const {
    account,
    onClose,
    open,
  } = props;
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const formContext = useForm({
    defaultValues: getAvailabilityPreferenceDefault(account?.unavailableStatuses, account),
    resolver: yupResolver(getAvailabilityPreferenceSchema(t, account)),
  });

  const pullCalendarsField = useFieldArray({
    control: formContext.control,
    name: 'checkCalendars',
    keyName: 'rhfId',
  });

  const currentTabView = formContext.watch('meta.currentTabView');

  const calendarOptions = pullCalendarsField.fields;
  const maxCheckCalendarsNum = MAX_CHECK_CALENDARS[account?.service] || 4;
  const checkedCalendarOptions = (calendarOptions || []).filter((option) => option.checked);
  const checkCalError = formContext.formState.errors?.checkCalendars?.message;

  useEffect(() => {
    formContext.reset(getAvailabilityPreferenceDefault(account?.unavailableStatuses, account));
  }, [account, formContext]);

  const [changeLoading, setChangeLoading] = useState(false);

  const resetState = useCallback(() => {
    formContext.reset(getAvailabilityPreferenceDefault(account?.unavailableStatuses, account));
  }, [formContext, account]);

  useEffect(() => {
    if (checkCalError) {
      formContext.setValue('meta.currentTabView', CONFLICT_SETTING_VIEW.CALENDARS);
    }
  }, [checkCalError, formContext]);

  const onSubmit = useCallback(() => {
    setChangeLoading(true);
    formContext.handleSubmit(async (formValues) => {
      try {
        console.log('submitting event status', formValues);
        await dispatch(updateAvailabilityPreferences({
          values: formValues,
          accountEmail: account.email,
          accountService: account.service,
        })).unwrap();
        onClose();
      } catch (e) {
        console.error('Encounter unexpected error when updating availability:', e);
        formContext.setError('root.server', {type: 'server', message: 'integration.genericSettingOpFailed'});
      } finally {
        setChangeLoading(false);
      }
    }, () => {
      setChangeLoading(false);
    })();
  }, [account, dispatch, formContext, onClose]);

  return (
    <FormProvider {...formContext}>
      <Backdrop open={changeLoading}
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
      </Backdrop>
      <ZmDialog sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '400px',
          },
          'padding': '20px',
        },
      }} open={open && !changeLoading}>
        <DialogTitle sx={{
          fontWeight: '700',
        }}>{t('integration.availabilityPreference')}</DialogTitle>
        <DialogContent>
          <TypographyTitle>{t('common.account')}</TypographyTitle>
          <AccountInfoContainer>
            <SvgIcon
              style={{width: '48px', height: '48px'}}
              component={iconMap[account?.service]}
              inheritViewBox
            ></SvgIcon>
            <div>
              <RowNameAndService>
                  ({serviceNameMap[account?.service]})
              </RowNameAndService>
              <RowEmailAdress>{account?.email}</RowEmailAdress>
            </div>
          </AccountInfoContainer>
          <Divider key="menu-divider" sx={{margin: 0}} aria-hidden={true}/>

          {/*  Only MS 0365 Accounts supports setting unavailable event types */}
          {account?.service === PROVIDER_TYPE.OUTLOOK_CALENDAR &&
            <ZmSliderTabs
              value={currentTabView}
              sx={{marginTop: '16px'}}
              onChange={(e, newVal) => formContext.setValue('meta.currentTabView', newVal)}
            >
              <Tab label={t('integration.calendarSubscriptions')} value={CONFLICT_SETTING_VIEW.CALENDARS} />
              <Tab label={t('integration.eventTypes')} value={CONFLICT_SETTING_VIEW.EVENTS} />
            </ZmSliderTabs>
          }

          {currentTabView === CONFLICT_SETTING_VIEW.CALENDARS ?
            (
              <ModalContentContainer>
                <Box sx={{margin: '16px 0'}}>
                  <TypographyTitle>
                    {t('integration.checkCalendars')}
                  </TypographyTitle>
                  {account?.service &&
                    <TypographyDesc>
                      {t('integration.maxCheckCalendarNum', {count: maxCheckCalendarsNum})}
                    </TypographyDesc>
                  }
                  {(checkCalError) &&
                    <FormHelperText error sx={{marginTop: '8px'}}>
                      {checkCalError}
                    </FormHelperText>
                  }
                </Box>
                <Box sx={{maxHeight: 180, overflowY: 'auto'}}>
                  {(pullCalendarsField.fields || []).map((cal, i) => {
                    return (
                      <Controller
                        name={`checkCalendars.${i}`}
                        control={formContext.control}
                        render={({field}) => {
                          const disableCheckbox = !cal.checked && checkedCalendarOptions.length >= maxCheckCalendarsNum;
                          return (
                            <FormControlLabel
                              key={cal.id}
                              disabled={disableCheckbox}
                              sx={{marginLeft: '-8px', display: 'block'}}
                              control={
                                <ZmCheckbox
                                  {...field}
                                  checked={cal?.checked}
                                  onChange={(e) => pullCalendarsField.update(i, {
                                    ...cal,
                                    checked: e.target.checked,
                                  })}
                                  sx={{
                                    padding: 0,
                                    marginRight: '8px',
                                    ...disableCheckbox ?
                                    {opacity: '0.6'} :
                                    null,
                                  }}
                                />
                              }
                              label={cal.name}
                            />
                          );
                        }}
                      />
                    );
                  })}
                </Box>
              </ModalContentContainer>
            ) :
              <ModalContentContainer>
                <TypographyTitle sx={{margin: '16px 0'}}>
                  {t('integration.availabilityHeader')}
                </TypographyTitle>
                <FormControlLabel
                  control={
                    <Controller
                      name="busyEventTypes.busy"
                      control={formContext.control}
                      render={({field: props}) => (
                        <ZmCheckbox
                          checked={props.value}
                          disabled={true}
                          {...props}
                          sx={{
                            'padding': 0,
                            'marginRight': '8px',
                            'opacity': 0.3,
                          }}
                        />
                      )}
                    />
                  }
                  label={'Busy'}
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="busyEventTypes.tentative"
                      control={formContext.control}
                      render={({field: props}) => (
                        <ZmCheckbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                          sx={{
                            padding: 0,
                            marginRight: '8px',
                          }}
                        />
                      )}
                    />
                  }
                  label={'Tentative'}
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="busyEventTypes.workingElsewhere"
                      control={formContext.control}
                      render={({field: props}) => (
                        <ZmCheckbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                          sx={{
                            padding: 0,
                            marginRight: '8px',
                          }}
                        />
                      )}
                    />
                  }
                  label={'Working Elsewhere'}
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="busyEventTypes.oof"
                      control={formContext.control}
                      render={({field: props}) => (
                        <ZmCheckbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                          sx={{
                            padding: 0,
                            marginRight: '8px',
                          }}
                        />
                      )}
                    />
                  }
                  label={'Out of Office'}
                />
              </ModalContentContainer>
          }
        </DialogContent>
        <DialogActions>
          <>
            <ZmPrimaryBtn
              size="small"
              onClick={() => {
                onSubmit();
              }}
              disabled={changeLoading}
            >
              { t('common.save')}
            </ZmPrimaryBtn>
            <ZmSecondaryBtn
              size="small"
              onClick={() => {
                onClose();
                resetState();
              }}
            >
              {t('common.cancel')}
            </ZmSecondaryBtn>
          </>
        </DialogActions>
      </ZmDialog>
    </FormProvider>
  );
};
